import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import FighterCard from '../components/FighterCard';
import WinProbability from '../components/WinProbability';
import StatComparison from '../components/StatComparison';
import RecentFights from '../components/RecentFights';
import StyleBreakdown from '../components/StyleBreakdown';
import StyleMatchupMatrix from '../components/StyleMatchupMatrix';
import PredictionExplanation from '../components/PredictionExplanation';

const API = 'https://ufc-backend-nmib.onrender.com/api';

export default function Matchup() {
  const [searchParams] = useSearchParams();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  const f1Id = searchParams.get('f1');
  const f2Id = searchParams.get('f2');

  useEffect(() => {
    if (!f1Id || !f2Id) return;
    setLoading(true);
    setError(null);
    axios.get(`${API}/matchup/`, { params: { f1: f1Id, f2: f2Id } })
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.error || 'Failed to load matchup data.'))
      .finally(() => setLoading(false));
  }, [f1Id, f2Id]);

  return (
    <>
      <div className="header">
        <div className="container">
          <h1>UFC <span>Fight</span> Predictor</h1>
        </div>
      </div>

      <div className="container">
        <Link to="/" className="back-link">← Back to search</Link>

        {loading && <div className="loading">Loading matchup data...</div>}
        {error   && <div className="error-msg">{error}</div>}

        {data && (
          <>
            {/* Fighter headers */}
            <div className="matchup-header">
              <FighterCard fighter={data.fighter1} side="f1" />
              <div className="vs-badge">VS</div>
              <FighterCard fighter={data.fighter2} side="f2" />
            </div>

            {/* Tale of the Tape — analyst breakdown */}
            <PredictionExplanation
              fighter1={data.fighter1}
              fighter2={data.fighter2}
            />

            {/* Win probability */}
            <WinProbability
              prediction={{ ...data.prediction, style_matchup: data.style_matchup }}
              fighter1={data.fighter1}
              fighter2={data.fighter2}
            />

            {/* Style breakdown — side by side */}
            <div className="card">
              <h2>Fighting Style Breakdown</h2>
              <div className="breakdown-grid">
                <StyleBreakdown
                  breakdowns={data.breakdowns1}
                  fighterName={data.fighter1?.full_name}
                  side="f1"
                />
                <StyleBreakdown
                  breakdowns={data.breakdowns2}
                  fighterName={data.fighter2?.full_name}
                  side="f2"
                />
              </div>
            </div>

            {/* Style matchup matrix */}
            <div className="card">
              <h2>Dominant Fight Style Historical Matchups</h2>
              <p className="card-sub">
                Win % when F1's style (row) faces F2's style (column). Top 5 styles for each fighter shown.
              </p>
              <StyleMatchupMatrix
                breakdowns1={data.breakdowns1}
                breakdowns2={data.breakdowns2}
                f1Name={data.fighter1?.full_name?.split(' ')[1] || data.fighter1?.full_name}
                f2Name={data.fighter2?.full_name?.split(' ')[1] || data.fighter2?.full_name}
              />
            </div>

            {/* Stats comparison */}
            <StatComparison
              stats1={{ All: data.stats1 }}
              stats2={{ All: data.stats2 }}
              fighter1={data.fighter1}
              fighter2={data.fighter2}
            />

            {/* Recent fights */}
            <RecentFights
              fights1={data.recent_fights1}
              fights2={data.recent_fights2}
              fighter1={data.fighter1}
              fighter2={data.fighter2}
            />
          </>
        )}
      </div>
    </>
  );
}
